/**
 * v4.11.0 — Fleet Overview Panels (bottom strip).
 *
 * Three calm panels:
 *   - Schedule Mix       (stacked horizontal bar of current routing destinations)
 *   - Top Locations      (ranked list of taps_today)
 *   - Fleet Health Donut (active / static / inactive / expired)
 *
 * All values come from /api/card-fleet/overview. No new computation here.
 */
import { useTranslation } from "react-i18next";
import PlatformIcon, { platformColor } from "./PlatformIcon";

const HEALTH_COLOR = {
    active: "#10b981",
    static: "#3b82f6",
    inactive: "#f59e0b",
    expired: "#ef4444",
};

const Panel = ({ title, testId, children }) => (
    <div
        data-testid={testId}
        className="rounded-xl border p-4 sm:p-5 flex-1 min-w-0"
        style={{
            background: "rgba(15, 23, 42, 0.55)",
            borderColor: "rgba(148, 163, 184, 0.12)",
        }}
    >
        <h3
            className="text-sm font-semibold text-slate-100 mb-3"
            style={{ fontFamily: "Manrope, sans-serif" }}
        >
            {title}
        </h3>
        {children}
    </div>
);

const ScheduleMix = ({ mix }) => {
    const { t } = useTranslation();
    if (!mix || mix.length === 0) {
        return <p className="text-xs text-slate-500">{t("card_fleet.overview.no_data")}</p>;
    }
    return (
        <div data-testid="schedule-mix">
            <div
                className="flex h-6 rounded-md overflow-hidden border"
                style={{ borderColor: "rgba(148, 163, 184, 0.18)" }}
            >
                {mix.map((m) => (
                    <div
                        key={m.platform}
                        title={`${m.platform} · ${m.pct}%`}
                        style={{
                            width: `${m.pct}%`,
                            background: platformColor(m.platform),
                        }}
                    />
                ))}
            </div>
            <ul className="mt-3 space-y-1">
                {mix.slice(0, 5).map((m) => (
                    <li key={m.platform} className="flex items-center justify-between text-xs">
                        <span className="inline-flex items-center gap-1.5 text-slate-300">
                            <PlatformIcon platform={m.platform} size={12} brand />
                            {t(`card_fleet.platforms.${m.platform}`, { defaultValue: m.platform })}
                        </span>
                        <span className="font-mono text-slate-200">
                            {m.pct}% <span className="text-slate-500">· {m.count}</span>
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
};

const TopLocations = ({ locations }) => {
    const { t } = useTranslation();
    if (!locations || locations.length === 0) {
        return <p className="text-xs text-slate-500">{t("card_fleet.overview.no_top_locations")}</p>;
    }
    return (
        <ol className="space-y-1.5" data-testid="top-locations">
            {locations.map((loc, i) => (
                <li key={loc.location_id || i}
                    className="flex items-center justify-between text-xs">
                    <span className="inline-flex items-center gap-2 text-slate-200 min-w-0">
                        <span className="text-slate-500 w-4 text-right">{i + 1}.</span>
                        <span className="truncate">{loc.name}</span>
                    </span>
                    <span className="font-mono text-slate-200">
                        {(loc.taps_today || 0).toLocaleString()}
                    </span>
                </li>
            ))}
        </ol>
    );
};

const FleetHealthDonut = ({ health }) => {
    const { t } = useTranslation();
    const entries = ["active", "static", "inactive", "expired"]
        .map((k) => ({ key: k, count: (health && health[k]) || 0 }));
    const total = entries.reduce((s, e) => s + e.count, 0);
    if (total === 0) {
        return <p className="text-xs text-slate-500">{t("card_fleet.overview.no_data")}</p>;
    }
    // Build a conic-gradient donut.
    let cursor = 0;
    const stops = entries.map((e) => {
        const pct = (e.count / total) * 100;
        const stop = `${HEALTH_COLOR[e.key]} ${cursor}% ${cursor + pct}%`;
        cursor += pct;
        return stop;
    }).join(", ");

    return (
        <div className="flex items-center gap-4" data-testid="fleet-health-donut">
            <div
                className="w-24 h-24 rounded-full relative flex-shrink-0"
                style={{ background: `conic-gradient(${stops})` }}
            >
                <div
                    className="absolute inset-2 rounded-full flex items-center justify-center text-center"
                    style={{ background: "rgb(15, 23, 42)" }}
                >
                    <div>
                        <p className="text-base font-semibold text-slate-100 leading-none">
                            {total.toLocaleString()}
                        </p>
                        <p className="text-[10px] text-slate-500 mt-0.5">
                            {t("card_fleet.overview.total")}
                        </p>
                    </div>
                </div>
            </div>
            <ul className="flex-1 space-y-1 text-xs">
                {entries.map((e) => (
                    <li key={e.key} className="flex items-center justify-between">
                        <span className="inline-flex items-center gap-1.5 text-slate-200">
                            <span
                                className="inline-block w-2 h-2 rounded-full"
                                style={{ background: HEALTH_COLOR[e.key] }}
                            />
                            {t(`card_fleet.health.${e.key}`)}
                        </span>
                        <span className="font-mono text-slate-300">
                            {e.count.toLocaleString()}
                            <span className="text-slate-500 ml-1">
                                ({total ? ((e.count / total) * 100).toFixed(1) : "0.0"}%)
                            </span>
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default function FleetOverviewPanels({ overview, loading }) {
    const { t } = useTranslation();
    const o = overview || {};
    return (
        <div
            className="grid gap-3 sm:gap-4 grid-cols-1 lg:grid-cols-3"
            data-testid="fleet-overview-panels"
        >
            <Panel title={t("card_fleet.overview.schedule_mix")} testId="panel-schedule-mix">
                {loading ? (
                    <p className="text-xs text-slate-500">{t("common.loading")}</p>
                ) : (
                    <ScheduleMix mix={o.schedule_mix} />
                )}
            </Panel>
            <Panel title={t("card_fleet.overview.top_locations")} testId="panel-top-locations">
                {loading ? (
                    <p className="text-xs text-slate-500">{t("common.loading")}</p>
                ) : (
                    <TopLocations locations={o.top_locations} />
                )}
            </Panel>
            <Panel title={t("card_fleet.overview.fleet_health")} testId="panel-fleet-health">
                {loading ? (
                    <p className="text-xs text-slate-500">{t("common.loading")}</p>
                ) : (
                    <FleetHealthDonut health={o.fleet_health} />
                )}
            </Panel>
        </div>
    );
}
